import { useEffect, useMemo, useState } from "react";
import { BarChart3, Download, Eye, Filter, Play, Plus, Table2, Trash2 } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { ReportView, type ReportResult } from "@ncp/screens";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, InlineAlert, Input, NativeSelect, Skeleton, Switch, notifySaveFailed, saveFailureText } from "@ncp/ui";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { ConditionBuilder, type ConditionField } from "../features/generic/ConditionBuilder";
import { useDefinition, useDefinitionList } from "../features/generic/api";
import { CASE_FIELDS, REPORT_CHARTS, REPORT_FNS, useReportExport, useReportPreview, useReportRun, type ReportBody, type ReportMeasure } from "../features/reports/api";
import type { RecordTypeBody } from "../features/records/api";
import { fieldLabel } from "../features/records/RecordForm";

type Ctx = EditorContext<ReportBody>;
type T = (k: string, o?: Record<string, unknown>) => string;

function lint(b: ReportBody, t: T) {
  const errors: string[] = []; const warnings: string[] = [];
  if (!b.sourceKey) errors.push(t("admin:reports.lint.source"));
  for (const m of b.measures) if (m.fn !== "count" && !m.field) errors.push(t("admin:reports.lint.measureField", { name: m.label || m.key }));
  if (b.chart !== "table" && b.groupBy.length === 0 && b.chart !== "kpi") warnings.push(t("admin:reports.lint.chartNeedsGroup"));
  return { errors, warnings };
}

/** Report designer: source, filter, grouping and measures or plain columns, chart; a live preview against real data, and CSV / Excel export. */
export function ReportDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<ReportBody>
      resource="reports"
      basePath="/reports"
      navLabel={t("admin:nav.reports")}
      icon={BarChart3}
      writePermission="platform.reports.write"
      docsAnchor="reports"
      help={<><p>{t("admin:reports.help.what")}</p><p>{t("admin:reports.help.export")}</p></>}
      tabs={[{ key: "design", label: t("admin:reports.tabs.design"), icon: Filter }, { key: "results", label: t("admin:reports.tabs.results"), icon: Table2 }]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:reports.summary", { source: b.sourceKey || "—", chart: t(`admin:reports.charts.${b.chart}`) })}
      render={(tab, ctx) => (tab === "design" ? <DesignTab ctx={ctx} /> : <ResultsTab ctx={ctx} />)}
    />
  );
}

function useSourceFields(body: ReportBody): { fields: ConditionField[]; dateFields: string[] } {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const type = useDefinition<RecordTypeBody>("record-types", body.source === "records" && body.sourceKey ? body.sourceKey : undefined);
  return useMemo(() => {
    if (body.source === "cases") {
      const fields = CASE_FIELDS.map((k) => ({ key: k, label: t(`admin:reports.caseFields.${k}`), type: k === "createdAt" ? "date" : k === "isTest" ? "boolean" : "text" }));
      return { fields, dateFields: ["createdAt"] };
    }
    const own = (type.data?.fields ?? []).map((f) => ({ key: f.key, label: fieldLabel(f, locale), type: f.type, options: f.options?.map((o) => ({ value: o.value, label: locale === "ar" && o.labelAr ? o.labelAr : o.label })) }));
    const fields: ConditionField[] = [{ key: "number", label: t("admin:recordTypes.records.number"), type: "text" }, { key: "createdAt", label: t("admin:reports.caseFields.createdAt"), type: "date" }, ...own];
    return { fields, dateFields: fields.filter((f) => f.type === "date").map((f) => f.key) };
  }, [body.source, locale, t, type.data]);
}

function DesignTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const { body, patch, readOnly, definition } = ctx;
  const recordTypes = useDefinitionList("record-types");
  const services = useDefinitionList("services", {}, can("platform.services.read"));
  const { fields, dateFields } = useSourceFields(body);
  const grouped = body.groupBy.length > 0 || body.measures.length > 0;
  const numeric = fields.filter((f) => f.type === "number" || f.type === "money");
  const preview = useReportPreview();
  const [result, setResult] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    try { setResult(await preview.mutateAsync({ ...body, key: definition.key })); } catch (e) { setError(saveFailureText(e) || null); setResult(null); }
  };
  useEffect(() => { if (body.sourceKey) void run(); /* first preview on open */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMeasure = (i: number, p: Partial<ReportMeasure>) => patch((b) => ({ ...b, measures: b.measures.map((m, j) => (j === i ? { ...m, ...p } : m)) }));
  const groupOptions = fields.flatMap((f) => (dateFields.includes(f.key) ? [{ key: `${f.key}:month`, label: `${f.label} · ${t("admin:reports.bucket.month")}` }, { key: `${f.key}:year`, label: `${f.label} · ${t("admin:reports.bucket.year")}` }, { key: f.key, label: f.label }] : [{ key: f.key, label: f.label }]));

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle>{t("admin:reports.source.title")}</CardTitle><CardDescription>{t("admin:reports.source.intro")}</CardDescription></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:reports.source.kind")}>
              <NativeSelect data-testid="report-source" value={body.source} disabled={readOnly} onChange={(e) => patch({ source: e.target.value as "records" | "cases", sourceKey: "", filters: [], groupBy: [], measures: [], columns: [] })}>
                <option value="records">{t("admin:reports.source.records")}</option>
                <option value="cases">{t("admin:reports.source.cases")}</option>
              </NativeSelect>
            </Field>
            <Field label={body.source === "records" ? t("admin:reports.source.recordType") : t("admin:reports.source.service")}>
              <NativeSelect data-testid="report-source-key" value={body.sourceKey} disabled={readOnly} onChange={(e) => patch({ sourceKey: e.target.value, filters: [], groupBy: [], measures: [], columns: [] })}>
                <option value="">—</option>
                {(body.source === "records" ? recordTypes.data : services.data)?.map((x) => <option key={x.key} value={x.key}>{locale === "ar" && x.nameAr ? x.nameAr : x.name}</option>)}
              </NativeSelect>
            </Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={body.includeTest} disabled={readOnly} onCheckedChange={(c) => patch({ includeTest: c })} /> {t("admin:reports.source.includeTest")}</label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t("admin:reports.filter.title")}</CardTitle><CardDescription>{t("admin:reports.filter.intro")}</CardDescription></CardHeader>
          <CardContent><ConditionBuilder testId="report-filter" value={Array.isArray(body.filters) && body.filters.length === 0 ? true : body.filters} fields={fields} readOnly={readOnly || !body.sourceKey} onChange={(f) => patch({ filters: f === true ? [] : f })} /></CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t("admin:reports.shape.title")}</CardTitle><CardDescription>{t("admin:reports.shape.intro")}</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Button size="sm" variant={grouped ? "outline" : "gold"} disabled={readOnly} onClick={() => patch({ groupBy: [], measures: [] })}><Table2 /> {t("admin:reports.shape.list")}</Button>
              <Button data-testid="report-grouped" size="sm" variant={grouped ? "gold" : "outline"} disabled={readOnly || !body.sourceKey} onClick={() => patch({ groupBy: body.groupBy.length ? body.groupBy : [groupOptions[0]?.key ?? ""], measures: body.measures.length ? body.measures : [{ key: "count", label: t("admin:reports.fns.count"), labelAr: null, fn: "count" }], columns: [] })}><BarChart3 /> {t("admin:reports.shape.grouped")}</Button>
            </div>

            {grouped ? (<>
              <Field label={t("admin:reports.shape.groupBy")}>
                <div className="flex flex-wrap gap-2">
                  {[0, 1].map((i) => (
                    <NativeSelect key={i} data-testid={`report-group-${i}`} value={body.groupBy[i] ?? ""} disabled={readOnly} className="w-56" onChange={(e) => patch((b) => { const next = [...b.groupBy]; if (e.target.value) next[i] = e.target.value; else next.splice(i, 1); return { ...b, groupBy: next.filter(Boolean) }; })}>
                      <option value="">{i === 0 ? "—" : t("admin:reports.shape.thenBy")}</option>
                      {groupOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </NativeSelect>
                  ))}
                </div>
              </Field>
              <div className="space-y-2">
                <div className="text-sm font-medium">{t("admin:reports.shape.measures")}</div>
                {body.measures.map((m, i) => (
                  <div key={i} className="grid grid-cols-[120px_1fr_1fr_1fr_auto] gap-2">
                    <NativeSelect aria-label={t("admin:reports.shape.fn")} value={m.fn} disabled={readOnly} onChange={(e) => { const fn = e.target.value as ReportMeasure["fn"]; setMeasure(i, { fn, key: `${fn}${m.field ? `_${m.field}` : ""}`, label: m.label || t(`admin:reports.fns.${fn}`) }); }}>
                      {REPORT_FNS.map((fn) => <option key={fn} value={fn}>{t(`admin:reports.fns.${fn}`)}</option>)}
                    </NativeSelect>
                    <NativeSelect aria-label={t("admin:reports.shape.field")} value={m.field ?? ""} disabled={readOnly || m.fn === "count"} onChange={(e) => setMeasure(i, { field: e.target.value || null, key: `${m.fn}_${e.target.value}` })}>
                      <option value="">—</option>
                      {numeric.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </NativeSelect>
                    <Input aria-label={t("admin:recordTypes.fields.label")} value={m.label} disabled={readOnly} placeholder={t("admin:recordTypes.fields.label")} onChange={(e) => setMeasure(i, { label: e.target.value })} />
                    <Input aria-label={t("admin:recordTypes.fields.labelAr")} dir="rtl" value={m.labelAr ?? ""} disabled={readOnly} placeholder={t("admin:recordTypes.fields.labelAr")} onChange={(e) => setMeasure(i, { labelAr: e.target.value || null })} />
                    {!readOnly ? <Button size="sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, measures: b.measures.filter((_, j) => j !== i) }))}><Trash2 /></Button> : <span />}
                  </div>
                ))}
                {!readOnly ? <Button data-testid="report-add-measure" size="sm" variant="outline" onClick={() => patch((b) => ({ ...b, measures: [...b.measures, { key: `sum_${numeric[0]?.key ?? ""}`, label: t("admin:reports.fns.sum"), labelAr: null, fn: numeric.length ? "sum" : "count", field: numeric[0]?.key ?? null }] }))}><Plus /> {t("admin:reports.shape.addMeasure")}</Button> : null}
              </div>
            </>) : (
              <Field label={t("admin:reports.shape.columns")} hint={t("admin:reports.shape.columnsHint")}>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {fields.map((f) => {
                    const on = body.columns.some((c) => c.field === f.key);
                    return <label key={f.key} className="flex items-center gap-2 text-sm"><Checkbox checked={on} disabled={readOnly} onCheckedChange={(c) => patch((b) => ({ ...b, columns: c === true ? [...b.columns, { field: f.key }] : b.columns.filter((x) => x.field !== f.key) }))} /> {f.label}</label>;
                  })}
                </div>
              </Field>
            )}

            <Field label={t("admin:reports.shape.chart")}>
              <NativeSelect data-testid="report-chart" value={body.chart} disabled={readOnly} onChange={(e) => patch({ chart: e.target.value as ReportBody["chart"] })}>
                {REPORT_CHARTS.map((c) => <option key={c} value={c}>{t(`admin:reports.charts.${c}`)}</option>)}
              </NativeSelect>
            </Field>
          </CardContent>
        </Card>
      </div>

      <Card className="xl:sticky xl:top-40 xl:self-start">
        <CardHeader className="flex-row items-center justify-between">
          <div><CardTitle>{t("admin:reports.preview.title")}</CardTitle><CardDescription>{t("admin:reports.preview.intro")}</CardDescription></div>
          <Button data-testid="report-preview" size="sm" variant="gold" disabled={!body.sourceKey} loading={preview.isPending} onClick={run}><Play /> {t("admin:reports.preview.run")}</Button>
        </CardHeader>
        <CardContent>
          {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
          {preview.isPending && !result ? <Skeleton className="h-64" /> : result ? <ReportView result={result} locale={locale === "ar" ? "ar" : "en"} /> : <p className="text-sm text-muted-foreground">{t("admin:reports.preview.empty")}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function ResultsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const published = ctx.definition.status === "Published";
  const run = useReportRun(ctx.definition.key, published ? ctx.definition.version : undefined);
  const exportFile = useReportExport();
  const [busy, setBusy] = useState<string | null>(null);
  const download = async (format: "csv" | "xlsx") => {
    setBusy(format);
    try { await exportFile(ctx.definition.key, format, locale, published ? ctx.definition.version : undefined); } catch (e) { notifySaveFailed(e, t("admin:common.error")); } finally { setBusy(null); }
  };
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <div><CardTitle>{t("admin:reports.results.title")}</CardTitle><CardDescription>{published ? t("admin:reports.results.published", { version: ctx.definition.version }) : t("admin:reports.results.latest")}</CardDescription></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void run.refetch()} loading={run.isFetching}><Eye /> {t("admin:reports.results.refresh")}</Button>
          <Button data-testid="export-csv" size="sm" variant="outline" loading={busy === "csv"} onClick={() => void download("csv")}><Download /> CSV</Button>
          <Button data-testid="export-xlsx" size="sm" variant="gold" loading={busy === "xlsx"} onClick={() => void download("xlsx")}><Download /> Excel</Button>
        </div>
      </CardHeader>
      <CardContent>
        {run.isLoading ? <Skeleton className="h-64" /> : run.isError ? <InlineAlert tone="danger">{(run.error as Error).message}</InlineAlert> : run.data ? <ReportView result={run.data} locale={locale === "ar" ? "ar" : "en"} /> : null}
      </CardContent>
    </Card>
  );
}
